import React, { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { Trash, ShieldWarning, Check, X, Info } from "@phosphor-icons/react";

/**
 * Admin / work coach view of pending AVG-deletion requests (Art. 17).
 * - Admin sees all pending requests across the platform.
 * - Bedrijfsadmin sees own company.
 * - Regular work coach sees only own coachees.
 */
export default function DeletionRequestsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busyId, setBusyId] = useState("");
  const [rejectingId, setRejectingId] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/deletion-requests");
      setItems(data.requests || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleApprove = async (id, name) => {
    if (!window.confirm(`Bevestig: alle gegevens van ${name} worden definitief verwijderd. Doorgaan?`)) return;
    setBusyId(id);
    setError("");
    setInfo("");
    try {
      await api.post(`/admin/deletion-requests/${id}/approve`);
      setInfo(`Account en gegevens van ${name} zijn verwijderd.`);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyId("");
    }
  };

  const handleReject = async (id) => {
    if (!rejectReason.trim()) {
      setError("Geef een reden voor de afwijzing.");
      return;
    }
    setBusyId(id);
    setError("");
    setInfo("");
    try {
      await api.post(`/admin/deletion-requests/${id}/reject`, { reason: rejectReason.trim() });
      setInfo("Verzoek afgewezen.");
      setRejectingId("");
      setRejectReason("");
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyId("");
    }
  };

  const now = new Date();

  return (
    <AppLayout
      title="Verwijderverzoeken (AVG)"
      breadcrumbs={[{ label: "Dashboard", to: "/dashboard" }, { label: "Verwijderverzoeken" }]}
    >
      <div className="max-w-4xl space-y-4" data-testid="deletion-requests-page">
        <div className="neuro-card-bold p-5 bg-white">
          <div className="flex items-start gap-3">
            <ShieldWarning size={26} weight="duotone" className="text-[#E53935] shrink-0 mt-0.5" />
            <div>
              <h2 className="font-display text-lg font-bold tracking-tight">Openstaande AVG-verwijderverzoeken</h2>
              <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
                Onder de AVG hebben coachees recht op verwijdering van hun gegevens. Beoordeel elk verzoek
                binnen <strong>30 dagen</strong>. Na goedkeuring worden de gegevens onomkeerbaar gewist.
              </p>
            </div>
          </div>
        </div>

        {info && (
          <div className="border border-[#43A047]/30 bg-[#43A047]/5 text-[#43A047] text-sm px-4 py-3 rounded-sm" data-testid="deletion-requests-info">{info}</div>
        )}
        {error && (
          <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm" data-testid="deletion-requests-error">{error}</div>
        )}

        {loading ? (
          <div className="text-zinc-500 text-sm">Laden…</div>
        ) : items.length === 0 ? (
          <div className="neuro-card p-6 bg-white" data-testid="deletion-requests-empty">
            <div className="flex items-start gap-3">
              <Info size={22} className="text-[#1E88E5] shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display text-base font-bold tracking-tight">Geen openstaande verzoeken</h3>
                <p className="text-sm text-zinc-600 mt-1">Op dit moment zijn er geen AVG-verwijderverzoeken die je moet beoordelen.</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="deletion-requests-list">
            {items.map((r) => {
              const deadline = new Date(r.deadline);
              const overdue = deadline < now;
              const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
              return (
                <div
                  key={r.id}
                  className={"neuro-card p-5 bg-white " + (overdue ? "border-[#E53935] border-2" : "")}
                  data-testid={`deletion-request-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[260px]">
                      <h3 className="font-display text-base font-bold tracking-tight">{r.coachee_name}</h3>
                      <div className="text-xs text-zinc-500 mt-0.5">{r.coachee_email}</div>
                      <div className="mt-3 space-y-1 text-sm text-zinc-700">
                        <div>Ingediend op: <strong>{new Date(r.requested_at).toLocaleString()}</strong></div>
                        <div>
                          Deadline: <strong className={overdue ? "text-[#E53935]" : ""}>{deadline.toLocaleDateString()}</strong>
                          {overdue ? (
                            <span className="ml-2 inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide bg-[#E53935]/10 text-[#E53935] border border-[#E53935]/30 rounded-sm">Te laat</span>
                          ) : (
                            <span className="ml-2 text-xs text-zinc-500">(nog {daysLeft} dag{daysLeft === 1 ? "" : "en"})</span>
                          )}
                        </div>
                        {r.reason && (
                          <div className="mt-2 text-sm text-zinc-600 italic border-l-2 border-zinc-300 pl-3">
                            &ldquo;{r.reason}&rdquo;
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleApprove(r.id, r.coachee_name)}
                        disabled={busyId === r.id}
                        data-testid={`deletion-request-approve-${r.id}`}
                        className="inline-flex items-center gap-2 bg-[#E53935] text-white px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#C62828] transition-colors disabled:bg-zinc-400"
                      >
                        <Check size={16} /> Goedkeuren
                      </button>
                      <button
                        type="button"
                        onClick={() => { setRejectingId(rejectingId === r.id ? "" : r.id); setRejectReason(""); }}
                        disabled={busyId === r.id}
                        data-testid={`deletion-request-reject-${r.id}`}
                        className="inline-flex items-center gap-2 border-2 border-zinc-300 text-zinc-800 px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-zinc-100 transition-colors"
                      >
                        <X size={16} /> Afwijzen
                      </button>
                    </div>
                  </div>

                  {rejectingId === r.id && (
                    <div className="mt-4 border-t border-zinc-200 pt-4" data-testid={`deletion-request-reject-form-${r.id}`}>
                      <label className="text-xs uppercase tracking-widest text-zinc-500 block mb-1">Reden voor afwijzing</label>
                      <textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={2}
                        maxLength={1000}
                        data-testid={`deletion-request-reject-reason-${r.id}`}
                        className="w-full px-3 py-2 border-2 border-zinc-300 bg-white rounded-sm text-sm resize-none focus:outline-none focus:border-zinc-900"
                      />
                      <button
                        type="button"
                        onClick={() => handleReject(r.id)}
                        disabled={busyId === r.id || !rejectReason.trim()}
                        data-testid={`deletion-request-reject-submit-${r.id}`}
                        className="mt-2 inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:bg-zinc-400"
                      >
                        Afwijzing bevestigen
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
